/**
 * Edge Function Error Service
 * 
 * Centralized error handling for Edge Function calls with:
 * - Sentry integration (breadcrumbs + events)
 * - Amplitude metrics
 * - app_logs logging
 * - Deduplication (30-second window)
 * - Error type classification
 */

import * as Sentry from '@sentry/react';
import { logger } from './logger';

// Avoid circular import - we'll track analytics manually
// import { analyticsService } from './analyticsService';

// ============= CONSTANTS =============

const DEDUP_WINDOW_MS = 30_000; // 30 seconds between same errors
const errorDedup = new Map<string, number>();

// ============= TYPES =============

export type EdgeFunctionErrorType = 
  | 'network_error'        // TypeError: Load failed / Failed to fetch
  | 'functions_http_error' // FunctionsHttpError from Supabase
  | 'functions_relay_error' // FunctionsRelayError
  | 'rate_limited'         // 429 Too Many Requests
  | 'timeout'              // AbortError
  | 'auth_error'           // 401/403 errors
  | 'unknown';

export interface EdgeFunctionErrorContext {
  retryCount?: number;
  payload?: Record<string, unknown>;
  route?: string;
  additionalData?: Record<string, unknown>;
}

export interface EdgeFunctionErrorDetails {
  type: EdgeFunctionErrorType;
  message: string;
  status?: number;
  responseBody?: string;
  isRetryable: boolean;
  originalError?: unknown;
}

interface EdgeFunctionErrorStats {
  totalErrors: number;
  byType: Record<EdgeFunctionErrorType, number>;
  byFunction: Record<string, number>;
  lastError?: {
    functionName: string;
    type: EdgeFunctionErrorType;
    timestamp: number;
  };
}

// ============= STATE =============

const stats: EdgeFunctionErrorStats = {
  totalErrors: 0,
  byType: {
    network_error: 0,
    functions_http_error: 0,
    functions_relay_error: 0,
    rate_limited: 0,
    timeout: 0,
    auth_error: 0,
    unknown: 0,
  },
  byFunction: {},
};

// ============= ERROR TYPE DETECTION =============

/**
 * Check if error is a network error (offline, DNS failure, etc.)
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) {
    const message = error.message.toLowerCase();
    return (
      message.includes('load failed') ||
      message.includes('failed to fetch') ||
      message.includes('network request failed') ||
      message.includes('networkerror')
    );
  }
  return false;
}

/**
 * Check if error is a Supabase FunctionsHttpError
 */
export function isFunctionsHttpError(error: unknown): boolean {
  if (error === null || typeof error !== 'object') return false;
  const err = error as { name?: string };
  return err.name === 'FunctionsHttpError';
}

/**
 * Check if error is a Supabase FunctionsRelayError
 */
export function isFunctionsRelayError(error: unknown): boolean {
  if (error === null || typeof error !== 'object') return false;
  const err = error as { name?: string };
  return err.name === 'FunctionsRelayError';
}

/**
 * Check if error is a timeout/abort error
 */
export function isTimeoutError(error: unknown): boolean {
  if (error instanceof Error) {
    return error.name === 'AbortError' || error.name === 'TimeoutError';
  }
  return false;
}

/**
 * Extract detailed error information
 */
export function extractErrorDetails(error: unknown): EdgeFunctionErrorDetails {
  // Network errors (offline, DNS, etc.)
  if (isNetworkError(error)) {
    return {
      type: 'network_error',
      message: 'Network request failed - user may be offline or having connectivity issues',
      isRetryable: true,
      originalError: error,
    };
  }
  
  // Supabase FunctionsHttpError
  if (isFunctionsHttpError(error)) {
    const httpError = error as { 
      message?: string;
      context?: { 
        status?: number; 
        body?: string;
        json?: unknown;
      };
    };
    
    const status = httpError.context?.status;
    const responseBody = httpError.context?.body;
    
    // Determine specific error type based on status
    let type: EdgeFunctionErrorType = 'functions_http_error';
    let isRetryable = false;
    
    if (status === 429) {
      type = 'rate_limited';
      isRetryable = true;
    } else if (status === 401 || status === 403) {
      type = 'auth_error';
      isRetryable = false;
    } else if (status && status >= 500) {
      isRetryable = true;
    }
    
    return {
      type,
      message: httpError.message || 'Edge function HTTP error',
      status,
      responseBody,
      isRetryable,
      originalError: error,
    };
  }
  
  // Supabase FunctionsRelayError
  if (isFunctionsRelayError(error)) {
    return {
      type: 'functions_relay_error',
      message: (error as Error).message || 'Edge function relay error',
      isRetryable: true,
      originalError: error,
    };
  }
  
  // Timeout errors
  if (isTimeoutError(error)) {
    return {
      type: 'timeout',
      message: 'Request timed out',
      isRetryable: true,
      originalError: error,
    };
  }
  
  // Unknown errors
  return {
    type: 'unknown',
    message: error instanceof Error ? error.message : String(error),
    isRetryable: false,
    originalError: error,
  };
}

// ============= DEDUPLICATION =============

function getDedupKey(functionName: string, errorType: EdgeFunctionErrorType, route: string): string {
  return `${functionName}:${errorType}:${route}`;
}

function shouldReport(key: string): boolean {
  const now = Date.now();
  const last = errorDedup.get(key);
  if (last && now - last < DEDUP_WINDOW_MS) return false;
  errorDedup.set(key, now);
  return true;
}

// ============= MAIN HANDLER =============

/**
 * Handle Edge Function error with full logging to Sentry, Amplitude, and app_logs
 */
export function handleEdgeFunctionError(
  functionName: string,
  error: unknown,
  context?: EdgeFunctionErrorContext
): void {
  const details = extractErrorDetails(error);
  const route = context?.route || (typeof window !== 'undefined' ? window.location.pathname : '/unknown');
  
  // Update stats
  stats.totalErrors++;
  stats.byType[details.type]++;
  stats.byFunction[functionName] = (stats.byFunction[functionName] || 0) + 1;
  stats.lastError = {
    functionName,
    type: details.type,
    timestamp: Date.now(),
  };
  
  const dedupKey = getDedupKey(functionName, details.type, route);
  const shouldLog = shouldReport(dedupKey);
  
  const eventData = {
    function_name: functionName,
    error_type: details.type,
    error_message: details.message,
    status: details.status,
    response_body: typeof details.responseBody === 'string' ? details.responseBody.substring(0, 500) : undefined,
    is_retryable: details.isRetryable,
    retry_count: context?.retryCount || 0,
    route,
    ...context?.additionalData,
  };
  
  // 1. Always add Sentry breadcrumb (even for deduplicated errors)
  Sentry.addBreadcrumb({
    category: 'edge_function',
    message: `Edge function error: ${functionName}`,
    level: 'error',
    data: {
      function_name: functionName,
      error_type: details.type,
      status: details.status,
      is_retryable: details.isRetryable,
    },
  });
  
  if (!shouldLog) {
    // Still track deduplicated errors in debug
    logger.debug(`[edgeFunctionErrorService] Deduplicated error for ${functionName}`, {
      context: { feature: 'edge_functions', function_name: functionName },
      data: { error_type: details.type },
      allowList: ['error_type'],
    });
    return;
  }
  
  // 2. Send to Sentry
  // Network errors → warning (expected on mobile/poor connection)
  // Other errors → error
  const sentryLevel = details.type === 'network_error' ? 'warning' : 'error';
  
  Sentry.captureMessage(`Edge function failed: ${functionName}`, {
    level: sentryLevel,
    tags: {
      function_name: functionName,
      error_type: details.type,
      is_retryable: String(details.isRetryable),
      http_status: details.status ? String(details.status) : undefined,
    },
    extra: eventData,
    fingerprint: ['edge_function_error', functionName, details.type],
  });
  
  // 3. Log to app_logs via logger
  logger.error(`Edge function ${functionName} failed`, {
    context: { 
      feature: 'edge_functions', 
      function_name: functionName,
      route,
    },
    data: eventData,
    allowList: [
      'function_name', 
      'error_type', 
      'status', 
      'is_retryable', 
      'retry_count',
      'route',
    ],
    error_code: `edge_function_${details.type}`,
    error_type: 'network',
  });
  
  // 4. Track in analytics (avoid circular import by using window event)
  // The analyticsService will listen for this event
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('edge_function_error', {
      detail: eventData,
    }));
  }
}

// ============= STATS =============

/**
 * Get error statistics
 */
export function getEdgeFunctionErrorStats(): EdgeFunctionErrorStats {
  return { ...stats };
}

/**
 * Clear error statistics
 */
export function clearEdgeFunctionErrorStats(): void {
  stats.totalErrors = 0;
  Object.keys(stats.byType).forEach(key => {
    stats.byType[key as EdgeFunctionErrorType] = 0;
  });
  stats.byFunction = {};
  stats.lastError = undefined;
}

/**
 * Clear deduplication cache (for testing)
 */
export function clearEdgeFunctionErrorDedup(): void {
  errorDedup.clear();
}

// ============= EXPORTS =============

export const edgeFunctionErrorService = {
  handleError: handleEdgeFunctionError,
  isNetworkError,
  isFunctionsHttpError,
  isFunctionsRelayError,
  isTimeoutError,
  extractDetails: extractErrorDetails,
  getStats: getEdgeFunctionErrorStats,
  clearStats: clearEdgeFunctionErrorStats,
  clearDedup: clearEdgeFunctionErrorDedup,
};
